import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, throwError, from, of } from 'rxjs';
import { catchError, expand, map, reduce, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { ApiResponse, ApiPaginatedResponse, ApiErrorCode } from './api.interface';
import { StaleUsersResponse } from 'shared_models';
import { User, UserRole, UpdateUserRequest, StaleUser, StaleUserDeletionResult, UserRequestPayload, UserRequestResponse, UserRequestSummary, UserRequestListResponse, UserRequestUpdatePayload, ApproveUserRequestResponse } from 'shared_models';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = environment.apiUrl;
  private userCache = new Map<string, User>();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private logger: LoggerService
  ) {}

  /** API uses `startKey` (from prior response `nextKey`) for user/company/project list pagination. */
  private getPaginationParams(limit?: number, startKey?: string): HttpParams {
    let params = new HttpParams();
    if (limit) params = params.set('limit', limit.toString());
    if (startKey) params = params.set('startKey', startKey);
    return params;
  }

  /** Arrow so `catchError(this.handleError)` keeps `this`. */
  private handleError = (error: HttpErrorResponse) => {
    this.logger.error('API Error:', error);
    return throwError(() => error);
  };

  /**
   * Lists all users matching filters by paging through GET /users until `nextKey` is empty.
   * Single-page access: {@link listUsers}.
   */
  getUsers(options?: {
    role?: UserRole;
    companyID?: string;
    projectID?: string;
    /** Preferred page size for each underlying request (default 100). */
    limit?: number;
    /** Ignored — all pages are fetched. */
    startKey?: string;
    /** @deprecated Ignored — all pages are fetched. */
    lastEvaluatedKey?: string;
  }): Observable<ApiPaginatedResponse<User[]>> {
    return this.listAllUsers({
      role: options?.role != null ? String(options.role) : undefined,
      companyID: options?.companyID,
      projectID: options?.projectID,
      pageLimit: options?.limit,
    });
  }

  getCurrentUser(): Observable<ApiResponse<User>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/me`;
        this.logger.debug('[API OUT] GET', url, { headers });
        return this.http.get<ApiResponse<User>>(url, { headers })
          .pipe(
            tap(response => {
              this.logger.debug('[API IN] GET', url, response);
            }),
            catchError(error => {
              this.logger.error('API request error details:', {
                status: error.status,
                statusText: error.statusText,
                message: error.message,
                error: error.error,
                url: error.url,
                headers: error.headers,
                name: error.name
              });
              if (error.status === 0) {
                this.logger.error('CORS error detected. The API server may not be configured to accept requests from this origin.');
              }
              return throwError(() => error);
            })
          );
      }),
      catchError(this.handleError)
    );
  }

  getUser(userId: string): Observable<ApiResponse<User>> {
    // Check cache first
    if (this.userCache.has(userId)) {
      const cachedUser = this.userCache.get(userId)!;
      this.logger.debug('[CACHE HIT] User', userId, cachedUser);
      return of({ data: cachedUser, message: 'User loaded from cache', code: ApiErrorCode.Success });
    }

    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userId}`;
        this.logger.debug('[API OUT] GET', url, { headers });
        return this.http.get<ApiResponse<User>>(url, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] GET', url, response);
            // Cache the user data
            if (response.data) {
              this.userCache.set(userId, response.data);
            }
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  createUser(userData: Partial<User> & { temporaryPassword: string }): Observable<ApiResponse<User>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users`;
        this.logger.debug('[API OUT] POST', url, { headers, body: userData });
        return this.http.post<ApiResponse<User>>(url, userData, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] POST', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Creates a new user request for admin review. POST /users/requests
   */
  createUserRequest(payload: UserRequestPayload): Observable<ApiResponse<UserRequestResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/requests`;
        this.logger.debug('[API OUT] POST', url, { headers, body: payload });
        return this.http.post<ApiResponse<UserRequestResponse>>(url, payload, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] POST', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * List user requests with optional filters and pagination. GET /users/requests
   */
  listUserRequests(options?: { limit?: number; startKey?: string; companyID?: string; status?: string }): Observable<ApiResponse<UserRequestListResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams();
        if (options?.limit) params = params.set('limit', options.limit.toString());
        if (options?.startKey) params = params.set('startKey', options.startKey);
        if (options?.companyID) params = params.set('companyID', options.companyID);
        if (options?.status) params = params.set('status', options.status);
        const url = `${this.apiUrl}/users/requests`;
        this.logger.debug('[API OUT] GET', url, { headers, params: options });
        return this.http.get<ApiResponse<UserRequestListResponse>>(url, { headers, params }).pipe(
          tap(response => this.logger.debug('[API IN] GET', url, response))
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update a user request. PUT /users/requests/{requestId}
   */
  updateUserRequest(requestId: string, payload: UserRequestUpdatePayload): Observable<ApiResponse<{ message: string; requestId: string }>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/requests/${requestId}`;
        this.logger.debug('[API OUT] PUT', url, { headers, body: payload });
        return this.http.put<ApiResponse<{ message: string; requestId: string }>>(url, payload, { headers }).pipe(
          tap(response => this.logger.debug('[API IN] PUT', url, response))
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a user request. DELETE /users/requests/{requestId}
   */
  deleteUserRequest(requestId: string): Observable<ApiResponse<{ message: string; requestId: string }>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/requests/${requestId}`;
        this.logger.debug('[API OUT] DELETE', url, { headers });
        return this.http.delete<ApiResponse<{ message: string; requestId: string }>>(url, { headers }).pipe(
          tap(response => this.logger.debug('[API IN] DELETE', url, response))
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Approve a user request and create the user in Cognito. POST /users/requests/{requestId}/approve (GlobalAdmin only)
   */
  approveUserRequest(requestId: string): Observable<ApiResponse<ApproveUserRequestResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/requests/${requestId}/approve`;
        this.logger.debug('[API OUT] POST', url, { headers });
        return this.http.post<ApiResponse<ApproveUserRequestResponse>>(url, {}, { headers }).pipe(
          tap(response => this.logger.debug('[API IN] POST', url, response))
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * All users assigned to a project (every page of `GET /users?projectID=…`).
   * The legacy `GET /projects/{projectID}/users` route was removed from the API.
   */
  getProjectUsers(projectId: string, pageLimit?: number): Observable<ApiPaginatedResponse<User[]>> {
    return this.listAllUsers({ projectID: projectId, pageLimit });
  }

  /**
   * Users in a company via `GET /users?companyID={companyId}` (all pages unless `startKey` is set).
   * Replaces the removed `GET /companies/{companyID}/users` route.
   */
  getCompanyUsers(companyId: string, limit?: number, startKey?: string): Observable<ApiPaginatedResponse<User[]>> {
    if (startKey) {
      return this.listUsers({ companyID: companyId, limit: limit ?? 100, startKey });
    }
    return this.getUsers({ companyID: companyId, limit });
  }

  /**
   * Update the current user's profile information
   */
  updateCurrentUser(userData: UpdateUserRequest): Observable<ApiResponse<User>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/me`;
        this.logger.debug('[API OUT] PUT', url, { headers, body: userData });
        return this.http.put<ApiResponse<User>>(url, userData, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] PUT', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Update a user's information
   */
  updateUser(userData: Partial<User> & { id: string }): Observable<ApiResponse<User>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userData.id}`;
        this.logger.debug('[API OUT] PUT', url, { headers, body: userData });
        return this.http.put<ApiResponse<User>>(url, userData, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] PUT', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a user by userID
   */
  deleteUser(userID: string): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userID}`;
        this.logger.debug('[API OUT] DELETE', url, { headers });
        return this.http.delete<ApiResponse<any>>(url, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] DELETE', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Assign a role to a user (GlobalAdmin only)
   */
  assignRoleToUser(userID: string, role: UserRole): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userID}/role`;
        const body = { role };
        this.logger.debug('[API OUT] PUT', url, { headers, body });
        return this.http.put<ApiResponse<any>>(url, body, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] PUT', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * List users with optional filters (role, companyID, projectID, etc.)
   * Example usage: listUsers({ projectID: 'project-123' })
   */
  /**
   * List users via GET /users with optional role, companyID, projectID, limit, startKey.
   * Single endpoint so project and company filters can be combined (per API).
   */
  listUsers(options?: {
    role?: string;
    companyID?: string;
    projectID?: string;
    limit?: number;
    startKey?: string;
    /** @deprecated Use startKey */
    lastEvaluatedKey?: string;
  }): Observable<ApiPaginatedResponse<User[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let httpParams = new HttpParams();
        if (options?.role) httpParams = httpParams.set('role', options.role);
        if (options?.companyID) httpParams = httpParams.set('companyID', options.companyID);
        if (options?.projectID) httpParams = httpParams.set('projectID', options.projectID);
        if (options?.limit) httpParams = httpParams.set('limit', options.limit.toString());
        const cursor = options?.startKey ?? options?.lastEvaluatedKey;
        if (cursor) httpParams = httpParams.set('startKey', cursor);
        const url = `${this.apiUrl}/users`;
        this.logger.debug('[API OUT] GET', url, { headers, params: httpParams });
        return this.http.get<ApiPaginatedResponse<User[]>>(url, { headers, params: httpParams }).pipe(
          tap(response => {
            this.logger.debug('[API IN] GET', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Fetches every page of GET /users for the given filters, following `nextKey` until exhausted.
   * De-duplicates by `userID`. Returns one {@link ApiPaginatedResponse} with `nextKey` / `previousKey` empty.
   * For a single page only, use {@link listUsers}.
   */
  listAllUsers(options?: {
    role?: string;
    companyID?: string;
    projectID?: string;
    /** Per-request page size (default 100). */
    pageLimit?: number;
  }): Observable<ApiPaginatedResponse<User[]>> {
    const pageLimit = options?.pageLimit ?? 100;
    const filters = {
      role: options?.role,
      companyID: options?.companyID,
      projectID: options?.projectID,
    };
    return this.listUsers({ ...filters, limit: pageLimit }).pipe(
      expand((res) => {
        const nk = (res.nextKey ?? '').trim();
        if (!nk) {
          return EMPTY;
        }
        return this.listUsers({ ...filters, limit: pageLimit, startKey: nk });
      }),
      reduce(
        (acc, res) => {
          const batch = Array.isArray(res.data) ? res.data : [];
          for (const u of batch) {
            const id = u.userID;
            if (id && !acc.seen.has(id)) {
              acc.seen.add(id);
              acc.users.push(u);
            }
          }
          acc.message = res.message ?? acc.message;
          acc.code = res.code ?? acc.code;
          return acc;
        },
        {
          users: [] as User[],
          seen: new Set<string>(),
          message: undefined as string | undefined,
          code: undefined as ApiErrorCode | undefined,
        }
      ),
      map((acc) => ({
        data: acc.users,
        message: acc.message ?? 'Users retrieved successfully',
        code: acc.code ?? ApiErrorCode.Success,
        nextKey: '',
        previousKey: '',
        totalCount: acc.users.length,
      }))
    );
  }

  /**
   * Get external users (QC engineers and Viewers) who have previously worked with a company
   */
  getExternalUsers(companyId: string, limit?: number, lastEvaluatedKey?: string): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const params = this.getPaginationParams(limit, lastEvaluatedKey);
        const url = `${this.apiUrl}/companies/${companyId}/external-users`;
        this.logger.debug('[API OUT] GET', url, { headers, params });
        return this.http.get<ApiResponse<any>>(url, { headers, params }).pipe(
          tap(response => {
            this.logger.debug('[API IN] GET', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * List stale user invites with optional project assignment details
   * Stale users are defined as users in FORCE_CHANGE_PASSWORD status where createdAt equals lastModifiedAt (never logged in)
   */
  listStaleInvites(options?: {
    includeAssignments?: boolean;
    limit?: number;
    paginationToken?: string;
  }): Observable<ApiResponse<StaleUsersResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams();
        if (options?.includeAssignments) {
          params = params.set('includeAssignments', 'true');
        }
        if (options?.limit) {
          params = params.set('limit', options.limit.toString());
        }
        if (options?.paginationToken) {
          params = params.set('paginationToken', options.paginationToken);
        }

        const url = `${this.apiUrl}/users/stale-invites`;
        this.logger.debug('[API OUT] GET', url, { headers, params });
        return this.http.get<ApiResponse<StaleUsersResponse>>(url, { headers, params }).pipe(
          tap(response => {
            this.logger.debug('[API IN] GET', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a specific stale user and all their project assignments
   * Only deletes users that are truly stale (never logged in)
   */
  deleteStaleUser(userID: string): Observable<ApiResponse<StaleUserDeletionResult>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/stale-invites/${userID}`;
        this.logger.debug('[API OUT] DELETE', url, { headers });
        return this.http.delete<ApiResponse<StaleUserDeletionResult>>(url, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] DELETE', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }
} 